/* Paints the strike-through price and "-10%" label in `snippets/price-strike.liquid`
   from the discounts app proxy (`/apps/discounts/data`), and takes down the loading
   placeholder that hides the price until it has done so.

   Why this lives in one asset rather than in the snippet: `price-strike` renders
   once per product card and once per cart line, so an inline <script> there means
   one copy per price on the page (24 on a collection page). More importantly the
   snippet's markup is destroyed and rebuilt by the theme - the Section Rendering
   API replaces cart items on every cart change, and the collection grid is
   swapped wholesale on filter/load-more - so a script that only runs at parse
   time paints labels that are then wiped. This fetches once and re-paints
   whenever the DOM grows new prices.

   Server-rendered labels win: a `save_percent` passed into the snippet (the cart
   lines pass the `_subscription_discount_*` figure) renders in Liquid and is left
   alone here. This only touches prices that have a `data-sv-discount-key` and no
   label yet - the strike-through additionally needs `data-sv-price-cents` (absent
   on `show_range`'s min-max markup, which has no single price to strike), and
   falls back to the label alone when that is missing.

   The discounted figure is formatted from the price already in the element rather
   than from the store's `money_format` template - see formatMoneyLike() below.

   The Recharge subscription widget on the PDP is painted separately (its markup
   lives in a shadow root the app renders) - see paintRecharge() below.

   Not covered: the PDP variant change, where theme.min.js overwrites the whole of
   `[data-product-price]` with `T4SProductStrings.price_template` and no wrapper -
   the key is gone with it. That surface already has the theme's own percentage
   badge (`.t4s-badge-price`, settings.saletype == '1'), which recomputes on every
   variant switch. */
(function () {
  'use strict';

  var ENDPOINT = '/apps/discounts/data';
  var HOOK = '[data-sv-discount-key]';
  var LABEL = 'sv-price__save';
  var PENDING = 'sv-price--pending';
  var MAX_ATTEMPTS = 3;

  /* How long the loading placeholder may cover a price, from the theme setting
     (see snippets/render_bottom.liquid). Clamped rather than trusted: a stale
     settings_data.json can hold any value the schema once allowed, and this one
     decides how long a shopper might see no price. 0 disables the placeholder,
     which is also what a missing global means - this file is the fast path, not
     the guarantee, and it stays optional. The CSS carries the same deadline
     independently, so the two never need to agree at runtime. */
  var TIMEOUT_MS = (function () {
    var raw = window.SavvyShopify && window.SavvyShopify.discountTimeoutMs;
    var n = typeof raw === 'number' ? raw : parseInt(raw, 10);
    if (!isFinite(n) || n <= 0) return 0;
    return Math.min(n, 8000);
  })();

  var request = null;   // in-flight or successful fetch; never a failed one
  var index = null;     // Map(idString -> { percent } | { amountCents }), built once per fetch
  var attempts = 0;
  var painting = false; // guards the observer against our own DOM writes
  var scheduled = false;

  /* ---- the only app-shaped part of this file -------------------------------
     The proxy's payload shape was never pinned down when this was first written,
     and the two consumers disagreed about it: this file assumed an id-keyed map,
     while the debug script in `sections/main-collection.liquid` assumed an array
     of discount entries with nested, GID-encoded target ids. Rather than bet on
     one, `buildIndex` accepts both and flattens whatever it gets into a single
     id -> percent Map. Everything below the marker is shape-agnostic.

     If the real payload turns out to be something neither branch handles, this is
     the function to change - and `window.__svPriceStrike.index()` in the console
     will show what it managed to extract. */

  var WRAPPER_KEYS = ['discounts', 'items', 'data', 'results', 'nodes'];

  /* Keys that hold the products/variants a discount applies to. When an entry has
     any of these we harvest ids only from them, which keeps the discount's own id
     and any incidental numbers out of the index. Falls back to walking the whole
     entry when none are present. */
  var TARGET_KEYS = [
    'targets', 'target_ids', 'targetIds',
    'products', 'product_ids', 'productIds',
    'variants', 'variant_ids', 'variantIds',
    'entitled_product_ids', 'entitledProductIds',
    'entitled_variant_ids', 'entitledVariantIds'
  ];

  var PERCENT_KEYS = [
    'percent', 'percentage', 'discount_percent', 'discountPercent',
    'discount_percentage', 'discountPercentage', 'value', 'amount'
  ];

  /* Product ids are 13 digits and variant ids 14 on this store; prices are 6
     (184900 = $184.900 COP). Requiring 10+ keeps money out of the id index -
     the `\d{6,}` used by the main-collection debug script does not. */
  var ID_RE = /\d{10,}/g;

  /* Checks the nested `value.type` DiscountProxy actually sends ("amount" vs "percentage")
     first - `entry.type` is the *discount's* type ("DiscountAutomaticBasic"), not the value's,
     and matching against it here used to make this always return false for a real fixed-amount
     discount, letting its raw currency amount fall through to entryDiscount()'s generic key scan
     and get misread as a percentage (a $5 discount rendering as "-5%"). The flatter fallbacks
     stay for the hand-rolled/unknown payload shapes this file was written to also tolerate. */
  function isFixedAmount(entry) {
    var nested = entry && entry.value && (entry.value.type || entry.value.value_type || entry.value.valueType);
    var type = nested || (entry && (entry.value_type || entry.valueType || entry.type || entry.discount_type));
    if (typeof type !== 'string') return false;
    type = type.toLowerCase();
    return type.indexOf('fixed') !== -1 || type.indexOf('amount') !== -1;
  }

  /* Fixed-amount discounts carry `value.amount` in the shop's normal decimal currency units
     (Shopify's MoneyV2 convention, e.g. "5.00" for $5.00) - unlike everywhere else in this file,
     which works in cents (`data-sv-price-cents`, formatMoney). Converted here, the one place
     cents are actually needed for this discount type, rather than having DiscountProxy deviate
     from Shopify's own convention for a shape only this file consumes. */
  function amountCents(entry) {
    var raw = entry && entry.value && entry.value.amount;
    if (raw == null) return 0;
    var n = typeof raw === 'string' ? parseFloat(raw) : Number(raw);
    if (!isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
  }

  /* Returns a percentage as a number 0-100, or 0 when the entry carries none.
     Shopify's Admin API expresses percentage discounts as a 0-1 float
     (`DiscountPercentage.percentage` = 0.1 for 10%), while hand-rolled proxies
     usually send 10. Anything in (0, 1) is read as a fraction and scaled - which
     misreads a genuine 0.5% discount, a trade this store does not run. */
  function toPercent(raw) {
    if (raw == null) return 0;
    if (typeof raw === 'object') {
      for (var i = 0; i < PERCENT_KEYS.length; i++) {
        if (raw[PERCENT_KEYS[i]] != null) return toPercent(raw[PERCENT_KEYS[i]]);
      }
      return 0;
    }
    var n = typeof raw === 'string' ? parseFloat(raw.replace('%', '').trim()) : Number(raw);
    if (!isFinite(n) || n <= 0) return 0;
    if (n < 1) n = n * 100;
    return n > 100 ? 0 : n;
  }

  /* Returns { percent: N } for a percentage-off entry, { amountCents: N } for a fixed-amount
     one, or null for neither. A fixed amount has no percentage of its own - what it's worth
     depends on the price of whichever product it lands on - so it's kept as cents here and
     turned into a percentage per element, against that element's own price, in paint(). */
  function entryDiscount(entry) {
    if (typeof entry === 'number' || typeof entry === 'string') {
      var direct = toPercent(entry);
      return direct > 0 ? { percent: direct } : null;
    }
    if (!entry || typeof entry !== 'object') return null;
    if (isFixedAmount(entry)) {
      var cents = amountCents(entry);
      return cents > 0 ? { amountCents: cents } : null;
    }
    for (var i = 0; i < PERCENT_KEYS.length; i++) {
      if (entry[PERCENT_KEYS[i]] != null) {
        var p = toPercent(entry[PERCENT_KEYS[i]]);
        if (p > 0) return { percent: p };
      }
    }
    return null;
  }

  /* Walks `value` and collects every id-shaped number it finds, raw or inside a
     `gid://shopify/ProductVariant/123` string. */
  function collectIds(value, out, depth) {
    if (value == null || depth > 6) return out;
    if (typeof value === 'number') {
      if (value >= 1000000000) out.push(String(value));
      return out;
    }
    if (typeof value === 'string') {
      var found = value.match(ID_RE);
      if (found) {
        for (var i = 0; i < found.length; i++) out.push(found[i]);
      }
      return out;
    }
    if (typeof value === 'object') {
      var keys = Object.keys(value);
      for (var k = 0; k < keys.length; k++) collectIds(value[keys[k]], out, depth + 1);
    }
    return out;
  }

  function targetIds(entry) {
    var ids = [];
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      for (var i = 0; i < TARGET_KEYS.length; i++) {
        if (entry[TARGET_KEYS[i]] != null) collectIds(entry[TARGET_KEYS[i]], ids, 0);
      }
    }
    // No explicit target field - fall back to harvesting the whole entry.
    if (!ids.length) collectIds(entry, ids, 0);
    return ids;
  }

  /* Flattens any of the accepted payload shapes into Map(id -> { percent } | { amountCents }).
     Where several discounts target the same id: between two of the same kind, the bigger one
     wins; between a percent and an amount, the percent wins - it's a fixed, known number, while
     the amount's real percentage isn't resolved until paint() knows which element (and price)
     it's being weighed against. */
  function buildIndex(payload) {
    var map = new Map();
    if (!payload || typeof payload !== 'object') return map;

    /* Unwrap repeatedly, not once: real payloads nest these ({ data: { nodes: [...] } }
       is the usual shape of a GraphQL result forwarded verbatim by a proxy). Stops
       at the first array, or when nothing matches. */
    var body = payload;
    for (var hop = 0; hop < 4 && !Array.isArray(body); hop++) {
      var next = null;
      for (var w = 0; w < WRAPPER_KEYS.length; w++) {
        var inner = body[WRAPPER_KEYS[w]];
        if (inner && typeof inner === 'object') { next = inner; break; }
      }
      if (!next) break;
      body = next;
    }

    function put(id, discount) {
      if (!id || !discount) return;
      var prev = map.get(id);
      if (!prev) {
        map.set(id, discount);
      } else if (prev.percent != null && discount.percent != null) {
        if (discount.percent > prev.percent) map.set(id, discount);
      } else if (prev.amountCents != null && discount.amountCents != null) {
        if (discount.amountCents > prev.amountCents) map.set(id, discount);
      } else if (discount.percent != null) {
        map.set(id, discount);
      }
    }

    if (Array.isArray(body)) {
      for (var i = 0; i < body.length; i++) {
        var entry = body[i];
        var discount = entryDiscount(entry);
        if (!discount) continue;
        var ids = targetIds(entry);
        for (var j = 0; j < ids.length; j++) put(ids[j], discount);
      }
      return map;
    }

    // Id-keyed map: { "44359338885296": 10 } or { "gid://...": { percent: 10 } }
    var keys = Object.keys(body);
    for (var k = 0; k < keys.length; k++) {
      var key = keys[k];
      var discount2 = entryDiscount(body[key]);
      if (!discount2) continue;
      var keyIds = String(key).match(ID_RE);
      if (keyIds) {
        for (var m = 0; m < keyIds.length; m++) put(keyIds[m], discount2);
      }
    }
    return map;
  }
  /* ------------------------------------------------------------------------- */

  /* Only successful responses are cached. The previous version cached the
     rejected promise too - it resolved to null and every later repaint reused
     that, so one transient failure disabled the labels for the life of the page.
     Failures now clear the cache and let the next repaint retry, capped so a
     store without the proxy installed does not get a fetch per DOM mutation. */
  function load() {
    if (index) return Promise.resolve(index);
    if (request) return request;
    if (attempts >= MAX_ATTEMPTS) return Promise.resolve(null);
    attempts++;

    /* Abort at the same deadline the placeholder uses. Past it the answer is no
       longer wanted: the price is already back on screen, and a late repaint is
       the jump this whole mechanism exists to remove. An aborted try lands in the
       catch below like any other failure, so it counts against MAX_ATTEMPTS and a
       later repaint may still retry - a proxy that is merely slow once is not
       written off for the life of the page. */
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var deadline = null;
    if (controller && TIMEOUT_MS > 0) {
      deadline = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);
    }

    request = fetch(ENDPOINT, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal: controller ? controller.signal : undefined
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        var ct = r.headers.get('content-type') || '';
        if (ct.indexOf('json') === -1) {
          throw new Error('Respuesta no-JSON del proxy: ' + (ct || 'sin content-type'));
        }
        return r.json();
      })
      .then(function (payload) {
        if (deadline) clearTimeout(deadline);
        index = buildIndex(payload);
        request = null;
        if (!index.size) {
          console.warn('[price-strike] el proxy respondio pero no se reconocio ningun descuento. ' +
            'Revisa buildIndex() contra el payload:', payload);
        }
        return index;
      })
      .catch(function (err) {
        if (deadline) clearTimeout(deadline);
        var reason = err.name === 'AbortError'
          ? 'sin respuesta en ' + TIMEOUT_MS + 'ms (timeout del tema)'
          : err.message;
        console.warn('[price-strike] descuentos no disponibles (intento ' +
          attempts + '/' + MAX_ATTEMPTS + '):', reason);
        request = null; // let a later repaint retry
        return null;
      });

    return request;
  }

  /* Delegates to assets/savvy-formatmoney.js (loaded immediately before this file in
     render_bottom.liquid, so it is reliably present by the time this runs) rather than
     `Shopify.formatMoney` - see that file's header comment for why the platform global
     of that name can't be trusted here. Guarded rather than called directly: a future
     load-order change or a stripped-out savvy-formatmoney.js should fail closed (skip
     the strike-through, matching this file's existing fail-open style for a bad fetch)
     rather than throw.

     `formatMoneyLike` and not `formatMoney`: this store's rendered prices do not match
     the grouping its own `money_format` placeholder documents, so the discounted half
     of a strike-through came out with a different thousands separator from the struck
     half beside it. The example passed here is the price the server rendered into this
     very element, which is the one thing guaranteed to be formatted the way the rest
     of the storefront is. */
  function formatMoneyLike(cents, exampleText, exampleCents) {
    if (!isFinite(cents)) return null;
    if (!window.SavvyShopify || typeof window.SavvyShopify.formatMoneyLike !== 'function') return null;
    try {
      return window.SavvyShopify.formatMoneyLike(cents, exampleText, exampleCents);
    } catch (e) {
      return null;
    }
  }

  /* Rewrites the plain `<ins class="sv-price__now">` into the same was/now pair
     `price-strike.liquid` renders server-side for a real compare_at_price - the
     current (pre-discount) text becomes the struck-through "was" as-is, and only
     the new discounted amount needs formatting. Silently does nothing if the markup
     it expects (present whenever `data-sv-price-cents` is, since both come from the
     same Liquid branch) is not there, or if `data-sv-price-cents` itself is absent -
     the `show_range` min-max case, which does not emit it. */
  function strikeThrough(el, percent) {
    var wasCents = parseInt(el.getAttribute('data-sv-price-cents'), 10);
    if (!isFinite(wasCents) || wasCents <= 0) return;
    var nowEl = el.querySelector('.sv-price__now');
    if (!nowEl) return;
    var nowFormatted = formatMoneyLike(
      Math.round(wasCents * (1 - percent / 100)),
      nowEl.textContent,
      wasCents
    );
    if (nowFormatted == null) return;

    var was = document.createElement('del');
    was.className = 'sv-price__was';
    was.innerHTML = nowEl.innerHTML;
    nowEl.parentNode.insertBefore(was, nowEl);
    nowEl.innerHTML = nowFormatted;
  }

  /* Theme setting "Show '-X%' badge next to admin discount pricing" - only gates
     the badge span; the struck-through price itself is controlled separately by
     the setting that stops this script from loading at all. Read once per paint
     rather than cached at module scope so a theme-editor toggle takes effect on
     the next repaint. Missing global (script order changed) defaults to shown,
     matching the setting's own default. */
  function showBadge() {
    return !window.SavvyShopify || window.SavvyShopify.showDiscountBadge !== false;
  }

  /* A percentage discount is already a percentage. A fixed amount only becomes one against a
     specific price, so it needs `wasCents` (the same `data-sv-price-cents` strikeThrough()
     reads) - missing on the `show_range` min-max markup, same as a fixed amount can't strike
     that case's price either. Rejects a result over 100% (a misconfigured discount larger than
     the price itself) rather than showing a nonsense badge, matching toPercent()'s own guard. */
  function effectivePercent(discount, wasCents) {
    if (!discount) return 0;
    if (discount.percent != null) return Math.round(discount.percent);
    if (discount.amountCents != null) {
      if (!isFinite(wasCents) || wasCents <= 0) return 0;
      var pct = Math.round((discount.amountCents / wasCents) * 100);
      return pct > 0 && pct <= 100 ? pct : 0;
    }
    return 0;
  }

  /* Uncovers every price still under a placeholder, whatever the outcome - a hit,
     a miss, a dead proxy or a timeout all end with the shopper seeing a price.
     Document-wide rather than per-element because most prices carry no discount
     at all: paint() walks past those without touching them, and they would sit
     covered until the CSS deadline if this only cleared what was painted.

     No MutationObserver guard needed - it watches childList only, so a class
     change here cannot schedule another pass. */
  function clearPending() {
    var nodes = document.querySelectorAll('.' + PENDING);
    for (var i = 0; i < nodes.length; i++) nodes[i].classList.remove(PENDING);
  }

  function paint(map) {
    if (!map || !map.size) return;
    var nodes = document.querySelectorAll(HOOK);
    if (!nodes.length) return;

    var badge = showBadge();
    painting = true;
    try {
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (el.querySelector('.' + LABEL)) continue; // Liquid already rendered one
        var discount = map.get(el.getAttribute('data-sv-discount-key'));
        if (!discount) continue;
        var wasCents = parseInt(el.getAttribute('data-sv-price-cents'), 10);
        var percent = effectivePercent(discount, wasCents);
        if (percent <= 0) continue;
        strikeThrough(el, percent);
        if (!badge) continue;
        var span = document.createElement('span');
        span.className = LABEL;
        span.textContent = '-' + percent + '%';
        el.appendChild(span);
      }
    } finally {
      painting = false;
    }
  }

  /* ---- Recharge subscription widget (PDP) ----------------------------------
     The widget is the Recharge app's own Lit element, rendered into an open shadow
     root, so none of the `price-strike.liquid` markup above exists there. Only its
     "One-time" price is painted: Shopify discounts apply to one-time purchases unless
     the discount opts in to subscriptions, and the proxy payload does not say which
     ones do, so the subscription price stays exactly what Recharge computed. The
     "-X%" label follows the same theme setting as everywhere else (showBadge()).

     Lit owns the price node's text and rewrites it on variant change, so that node
     is never edited. It only gains a `part` name (`sv-price-was`, styled from
     price-strike.css through `::part()`), and the discounted figure and label go
     into sibling spans of ours (`sv-price-now`, `sv-price-save`). Every pass works
     out the desired state from scratch and writes only what differs, which is what
     lets the shadow-root observer below re-run on every widget render - including
     the one our own write causes - without looping. */

  var RC_WIDGET = 'recharge-subscription-widget';
  var RC_PRICE = '[part~="rc-purchase-option__price"]';
  var RC_WAS = 'sv-price-was';
  var RC_NOW = 'sv-price-now';
  var RC_SAVE = 'sv-price-save';
  var rcScheduled = false;

  /* variant id -> price in cents, from the JSON main-product.liquid emits beside
     the app block. Read on every pass: it is a few bytes, and quick view can add
     another product's copy at any time. */
  function variantPrices() {
    var out = {};
    var nodes = document.querySelectorAll('script[data-sv-variant-prices]');
    for (var i = 0; i < nodes.length; i++) {
      try {
        var data = JSON.parse(nodes[i].textContent);
        for (var k in data) {
          if (Object.prototype.hasOwnProperty.call(data, k)) out[k] = data[k];
        }
      } catch (e) { /* a bad block costs this widget its strike, nothing more */ }
    }
    return out;
  }

  /* Returns { now, percent } for the widget's selected variant, or null when it has
     no discount. `variantId`/`productId` are the widget's declared properties and
     follow the selected variant; the attributes are what the theme rendered, kept
     as a fallback for a widget version that renames the properties. */
  function rechargeDiscount(widget, priceEl, map, prices) {
    if (!map || !map.size) return null;
    var vid = String(widget.variantId || widget.getAttribute('default-variant-id') || '');
    var pid = String(widget.productId || widget.getAttribute('product-id') || '');
    var discount = map.get(vid) || map.get(pid);
    if (!discount) return null;
    var wasCents = prices[vid];
    var percent = effectivePercent(discount, wasCents);
    if (percent <= 0) return null;
    /* The example text is the widget's own rendering of `wasCents`, so a price map
       that has gone stale against it makes formatMoneyLike return null - no strike
       - rather than strike a figure that does not match the one on screen. */
    var now = formatMoneyLike(Math.round(wasCents * (1 - percent / 100)), priceEl.textContent.trim(), wasCents);
    return now == null ? null : { now: now, percent: percent };
  }

  /* Puts a span carrying `part` right after `anchor` with `text`, or removes it
     when `text` is null. Touches the DOM only when something differs. */
  function syncSibling(root, anchor, part, text) {
    var el = root.querySelector('[part~="' + part + '"]');
    if (text == null) {
      if (el) el.parentNode.removeChild(el);
      return null;
    }
    if (!el || el.previousElementSibling !== anchor) {
      if (el) el.parentNode.removeChild(el);
      el = document.createElement('span');
      el.setAttribute('part', part);
      anchor.parentNode.insertBefore(el, anchor.nextSibling);
    }
    if (el.textContent !== text) el.textContent = text;
    return el;
  }

  function paintRecharge(map) {
    var widgets = document.querySelectorAll(RC_WIDGET);
    if (!widgets.length) return;
    var prices = variantPrices();
    var badge = showBadge();
    for (var i = 0; i < widgets.length; i++) {
      var root = widgets[i].shadowRoot;
      var priceEl = root && root.querySelector(RC_PRICE);
      if (!priceEl || !priceEl.part) continue; // no ::part support, nothing to style it with
      var d = rechargeDiscount(widgets[i], priceEl, map, prices);

      if (d) {
        if (!priceEl.part.contains(RC_WAS)) priceEl.part.add(RC_WAS);
      } else if (priceEl.part.contains(RC_WAS)) {
        priceEl.part.remove(RC_WAS);
      }
      var nowEl = syncSibling(root, priceEl, RC_NOW, d ? d.now : null);
      syncSibling(root, nowEl || priceEl, RC_SAVE, d && badge ? '-' + d.percent + '%' : null);
    }
  }

  /* The document-wide observer further down cannot see inside a shadow root, and
     the widget re-renders there on variant and purchase-option changes. One
     observer per widget, attached once; it schedules a Recharge-only pass so a
     widget re-render never re-runs paint() over the rest of the page. */
  function watchRecharge() {
    if (typeof MutationObserver !== 'function') return;
    var widgets = document.querySelectorAll(RC_WIDGET);
    for (var i = 0; i < widgets.length; i++) {
      var w = widgets[i];
      if (!w.shadowRoot || w.__svWatched) continue;
      w.__svWatched = true;
      new MutationObserver(refreshRecharge).observe(w.shadowRoot, {
        childList: true, subtree: true, characterData: true
      });
    }
  }

  function refreshRecharge() {
    if (rcScheduled) return;
    rcScheduled = true;
    setTimeout(function () {
      rcScheduled = false;
      watchRecharge();
      if (!document.querySelector(RC_WIDGET)) return;
      load().then(paintRecharge);
    }, 0);
  }
  /* ------------------------------------------------------------------------- */

  /* Coalesces the bursts of mutations a grid swap produces into one paint.
     setTimeout rather than requestAnimationFrame on purpose: rAF is paused while
     the tab is hidden, so a collection page opened in a background tab would sit
     unpainted until it was first focused. */
  function refresh() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      load().then(function (map) {
        /* `finally` in spirit: paint() bails early on an empty index or an empty
           page, and the placeholder has to come off in those cases too. */
        try {
          paint(map);
        } finally {
          clearPending();
        }
      });
    }, 0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }

  /* Belt to the CSS's braces. The stylesheet already uncovers the price on its
     own schedule and would do so even if this file were missing entirely, but
     that only changes how the placeholder looks - the class stays on the element.
     Removing it here keeps the DOM honest, so a later repaint, a screenshot or
     someone reading the markup does not find a "pending" price that has been
     visible for a while. Elements rendered after this fires (grid filtering,
     quick view) are covered by clearPending() on the next settled cycle, which by
     then resolves from cache. */
  if (TIMEOUT_MS > 0) setTimeout(clearPending, TIMEOUT_MS);

  /* Re-paint after the theme rebuilds cart markup. These are the events
     interactable.min.js already dispatches around `renderContents()`. */
  ['cart:refresh', 'cart:updated', 'add:cart:success', 'sidebar:updated'].forEach(function (name) {
    document.addEventListener(name, refresh);
  });

  /* ...and after everything else. The collection grid (filters, load-more,
     infinite scroll) is replaced with no event of its own - the theme dispatches
     nothing outside the cart vocabulary above - and quick view builds its markup
     straight into the drawer, so there is nothing to subscribe to for either.
     Watching for added nodes covers every surface at once, including ones added
     later, without this file having to know how each is rendered.

     `painting` keeps our own appendChild calls from scheduling another pass. */
  if (typeof MutationObserver === 'function') {
    var observer = new MutationObserver(function (records) {
      if (painting) return;
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(HOOK)) return refresh();
          if (node.querySelector && node.querySelector(HOOK)) return refresh();
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  /* Theme editor: re-rendering a section drops the labels with the old markup. */
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:load', refreshRecharge);

  /* The Recharge widget's script loads on its own schedule, before or after this
     one; its shadow root exists from the moment the element is defined, and the
     observer attached then catches every render after it. */
  if (window.customElements && typeof window.customElements.whenDefined === 'function') {
    window.customElements.whenDefined(RC_WIDGET).then(refreshRecharge);
  }

  /* Console handle for checking what came back without re-deploying. */
  window.__svPriceStrike = {
    refresh: refresh,
    refreshRecharge: refreshRecharge,
    clearPending: clearPending,
    timeoutMs: TIMEOUT_MS,
    index: function () { return index; },
    buildIndex: buildIndex
  };
})();
